import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Home,
  ListChecks,
  Users,
  Check,
  X,
  Edit,
  FileText,
  Eye,
  MoreVertical
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { PaginationControl } from '@/components/shared/PaginationControl';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import PropertyCard from '@/components/ui/PropertyCard';
import { PropertyType } from '@/types/property';
import { toast } from '@/hooks/use-toast';
import { useAuth } from "@/context/AuthContext";
import { getAdminPropertyStats, getAdminProperties, updatePropertyStatus } from '@/services/property';
import { getAdminUserStats } from '@/services/user';
import { getAdminInquiryStats, getAllAdminInquiries } from '@/services/inquiry';
import { pagination } from '@/constants';
import { encodeS3Url } from '@/components/encodeS3Url';
import { capitalizeWords } from '@/helper';

const AdminDashboard = () => {
  const { authUser } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const pendingApprovalsRef = React.useRef<HTMLDivElement>(null);
  const [pendingProperties, setPendingProperties] = useState<PropertyType[]>([]);
  const [recentProperties, setRecentProperties] = useState<PropertyType[]>([]);
  const [recentInquiries, setRecentInquiries] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);

  const [propertyStats, setPropertyStats] = useState({
    totalProperties: 0,
    published: 0,
    rejected: 0,
    pending: 0,
    draft: 0
  });

  const [userStats, setUserStats] = useState({
    totalUsers: 0,
    active: 0,
    inactive: 0
  });

  const [inquiryStats, setInquiryStats] = useState({
    totalInquiries: 0
  });

  const fetchAdminStats = async () => {
    try {
      const [propStats, usrStats, inqStats] = await Promise.all([
        getAdminPropertyStats(),
        getAdminUserStats(),
        getAdminInquiryStats()
      ]);

      setPropertyStats(propStats);
      setUserStats(usrStats);
      setInquiryStats(inqStats);
    } catch (error) {
      console.error("Error fetching admin stats:", error);
    }
  };

  const fetchPendingProperties = async (page = 1) => {
    try {
      const { data, pagination: paginationData } = await getAdminProperties({
        page: page,
        limit: pagination.limit,
        approvalStatus: 'PENDING',
      });
      if (data && Array.isArray(data)) {
        setPendingProperties(data);
        setTotalPages(paginationData?.totalPages || 1);
        setCurrentPage(page);
      } else {
        console.error("Unexpected API response format:", data);
        setPendingProperties([]);
      }
    } catch (error) {
      console.error("Error fetching pending properties:", error);
    }
  };

  const fetchRecentData = async () => {
    try {
      const [propsResponse, inqResponse] = await Promise.all([
        getAdminProperties({
          page: pagination.page,
          limit: pagination.recentLimit,
          approvalStatus: 'PUBLISHED',
        }), // Fetch recent properties
        getAllAdminInquiries({
          page: pagination.page,
          limit: pagination.recentLimit
        }) // Fetch recent inquiries
      ]);

      if (Array.isArray(propsResponse.data)) {
        setRecentProperties(propsResponse.data);
      } else {
        console.error("Unexpected API response format:", propsResponse.data);
      }

      if (Array.isArray(inqResponse.data)) {
        setRecentInquiries(inqResponse.data);
      } else {
        console.error("Unexpected API response format:", inqResponse.data);
      }
    } catch (error) {
      console.error("Error fetching recent data:", error);
    }
  };

  React.useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        await Promise.all([
          fetchAdminStats(),
          fetchPendingProperties(1),
          fetchRecentData()
        ]);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch pending properties helper to be used after updates
  const refreshPendingProperties = async () => {
    fetchPendingProperties(currentPage);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  // Pagination logic
  const currentPendingProperties = pendingProperties; // Already paginated from API

  // Approve property
  const approveProperty = async (id: string) => {
    try {
      await updatePropertyStatus(id, "PUBLISHED");
      toast({
        title: "Property Approved",
        description: "The property has been published to the website.",
      });
      refreshPendingProperties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to approve property",
        variant: "destructive"
      });
    }
  };

  // Reject property
  const rejectProperty = async (id: string) => {
    try {
      await updatePropertyStatus(id, "REJECTED");
      toast({
        title: "Property Rejected",
        description: "The broker has been notified about the rejection.",
      });
      refreshPendingProperties();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to reject property",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Header />
      <div className="pt-32 pb-20 bg-secondary/30 flex-1">
        <div className="container mx-auto px-4 md:px-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold">Admin Dashboard</h1>
              <div className="flex items-center gap-1 mt-1 mb-1">
                <span className="text-muted-foreground">Welcome back,</span>
                <span className="font-medium text-foreground">{capitalizeWords(authUser?.name || 'Admin')}</span>
              </div>
              <p className="text-muted-foreground">Manage properties, brokers, and platform content</p>
            </div>
            <div className="flex gap-2 flex-wrap">
              <Button asChild variant="outline">
                <Link to="/admin/property-management">
                  <Home className="h-4 w-4 mr-2" />
                  Properties
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/brokers">
                  <Users className="h-4 w-4 mr-2" />
                  Brokers
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/leads">
                  <ListChecks className="h-4 w-4 mr-2" />
                  Leads
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/blog">
                  <Edit className="h-4 w-4 mr-2" />
                  Blog
                </Link>
              </Button>
              <Button asChild variant="outline">
                <Link to="/admin/market-insights">
                  <FileText className="h-4 w-4 mr-2" />
                  Market Insights
                </Link>
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { title: 'Total Properties', value: propertyStats.totalProperties, icon: Home, desc: 'Total listed properties' },
              { title: 'Active Listings', value: propertyStats.published, icon: Check, desc: 'Properties currently live' },
              { title: 'Leads', value: inquiryStats.totalInquiries, icon: Users, desc: 'Total inquiries received' },
              { title: 'Active Brokers', value: userStats.active, icon: Users, desc: 'Active broker accounts' },
            ].map((stat, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">{stat.title}</CardTitle>
                  <stat.icon className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  {isLoading ? (
                    <Skeleton className="h-8 w-20 mb-1" />
                  ) : (
                    <div className="text-2xl font-bold">{stat.value}</div>
                  )}
                  {isLoading ? (
                    <Skeleton className="h-3 w-32" />
                  ) : (
                    <p className="text-xs text-muted-foreground">{stat.desc}</p>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Quick Approval Section */}
          <div className="mb-8" ref={pendingApprovalsRef}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold">Property Approvals</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setViewMode(viewMode === 'grid' ? 'table' : 'grid')}
              >
                {viewMode === 'grid' ? 'Table View' : 'Grid View'}
              </Button>
            </div>

            {isLoading ? (
              viewMode === 'grid' ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {[1, 2, 3].map((i) => (
                    <Card key={i} className="overflow-hidden">
                      <Skeleton className="h-48 w-full" />
                      <CardHeader className="pb-2">
                        <Skeleton className="h-6 w-3/4 mb-2" />
                        <Skeleton className="h-4 w-1/2" />
                      </CardHeader>
                      <CardContent className="pb-4">
                        <Skeleton className="h-8 w-full" />
                      </CardContent>
                      <CardFooter className="flex gap-2">
                        <Skeleton className="h-9 flex-1" />
                        <Skeleton className="h-9 flex-1" />
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="rounded-md border bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Property</TableHead>
                        <TableHead>Location</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead>Broker</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {[1, 2, 3, 4, 5].map((i) => (
                        <TableRow key={i}>
                          <TableCell>
                            <div className="flex items-center space-x-3">
                              <Skeleton className="h-10 w-10 rounded-md" />
                              <Skeleton className="h-4 w-32" />
                            </div>
                          </TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                          <TableCell className="text-right"><Skeleton className="h-8 w-8 ml-auto" /></TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            ) : currentPendingProperties.length > 0 ? (
              <>
                {viewMode === 'grid' ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {currentPendingProperties.map((property) => (
                      <PropertyCard
                        key={property._id}
                        property={property}
                        actions={
                          <div className="flex justify-between w-full gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => approveProperty(property._id)}
                              className="flex-1 bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                            >
                              <Check className="h-4 w-4 mr-2" />
                              Approve
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => rejectProperty(property._id)}
                              className="flex-1 bg-red-50 hover:bg-red-100 text-red-700 border-red-200"
                            >
                              <X className="h-4 w-4 mr-2" />
                              Reject
                            </Button>
                          </div>
                        }
                      />
                    ))}
                  </div>
                ) : (
                  <div className="rounded-md border bg-card">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Property</TableHead>
                          <TableHead>Location</TableHead>
                          <TableHead>Price</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Broker</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {currentPendingProperties.map((property) => (
                          <TableRow key={property._id}>
                            <TableCell>
                              <div className="flex items-center space-x-3">
                                <div className="h-10 w-10 rounded-md overflow-hidden bg-muted">
                                  {property.media?.images?.[0] ? (
                                    <img
                                      src={encodeS3Url(property.media.images[0])}
                                      alt={property.specifications?.titleDeed || 'Property'}
                                      className="h-full w-full object-cover"
                                    />
                                  ) : (
                                    <div className="h-full w-full flex items-center justify-center text-muted-foreground">
                                      <Home className="h-4 w-4" />
                                    </div>
                                  )}
                                </div>
                                <div>
                                  <div className="font-medium line-clamp-1">{capitalizeWords(property.specifications?.titleDeed || '')}</div>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{capitalizeWords(property.location?.locality || '')}</TableCell>
                            <TableCell>{formatPrice(property.pricing?.amount || 0)}</TableCell>
                            <TableCell>{property.specifications?.propertyType}</TableCell>
                            <TableCell>{capitalizeWords(property.createdBy?.name || 'Unknown')}</TableCell>
                            <TableCell className="text-right">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button variant="ghost" size="icon" className="h-8 w-8">
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuLabel>Manage Request</DropdownMenuLabel>
                                  <DropdownMenuItem asChild>
                                    <Link to={`/properties/${property.slug}`}>
                                      <Eye className="h-4 w-4 mr-2" />
                                      View
                                    </Link>
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => approveProperty(property._id)} className="text-green-600">
                                    <Check className="h-4 w-4 mr-2" />
                                    Approve
                                  </DropdownMenuItem>
                                  <DropdownMenuItem onClick={() => rejectProperty(property._id)} className="text-red-600">
                                    <X className="h-4 w-4 mr-2" />
                                    Reject
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            ) : (
              <Card>
                <CardContent className="py-8 text-center">
                  <p className="text-muted-foreground">No pending properties requiring approval.</p>
                </CardContent>
              </Card>
            )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <PaginationControl
                  totalPages={totalPages}
                  initialPage={currentPage}
                  onPageChange={fetchPendingProperties}
                />
              </div>
            )}
          </div>

          {/* Recent Activity Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle>Recent Published Properties</CardTitle>
                <CardDescription>Most recently published properties across the platform</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {isLoading ? (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="flex items-center space-x-4 rounded-md border p-4">
                        <Skeleton className="h-12 w-12 rounded-md" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-3/4" />
                          <Skeleton className="h-3 w-1/2" />
                        </div>
                        <Skeleton className="h-4 w-16" />
                      </div>
                    ))
                  ) : recentProperties.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No properties published yet.</p>
                  ) : (
                    recentProperties.map((property) => (
                      <Link
                        to={`/properties/${property.slug}`}
                        key={property._id}
                        className="flex items-center space-x-4 rounded-md border p-4 hover:bg-accent/50 transition-colors"
                      >
                        <div className="h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0 bg-muted">
                          {property.media?.images?.[0] ? (
                            <img
                              src={encodeS3Url(property.media.images[0])}
                              alt={property.specifications?.titleDeed || "Property"}
                              className="h-full w-full object-cover"
                            />
                          ) : (
                            <Home className="h-6 w-6 text-muted-foreground" />
                          )}
                        </div>
                        <div className="flex-1 space-y-1 min-w-0">
                          <p className="text-sm font-medium truncate">{capitalizeWords(property.specifications?.titleDeed || property.title || '')}</p>
                          <p className="text-xs text-muted-foreground truncate">
                            {property.location?.locality || 'N/A'} • Added {formatDate(property.createdAt || new Date().toISOString())}
                          </p>
                        </div>
                        <div className="text-sm font-medium whitespace-nowrap">
                          {formatPrice(property.pricing?.amount || 0)}
                        </div>
                      </Link>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Recent Inquiries</CardTitle>
                <CardDescription>Latest inquiries received</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {isLoading ? (
                    [1, 2, 3].map((i) => (
                      <div key={i} className="flex flex-col space-y-2 rounded-md border p-4">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-3 w-1/4" />
                        <Skeleton className="h-3 w-full" />
                      </div>
                    ))
                  ) : recentInquiries.length === 0 ? (
                    <p className="text-sm text-muted-foreground">No inquiries yet.</p>
                  ) : (
                    recentInquiries.map((inquiry) => (
                      <div key={inquiry._id} className="flex flex-col space-y-1 rounded-md border p-4">
                        <p className="text-sm font-medium">Client: {inquiry?.contactInfo?.name || 'Guest'}</p>
                        <p className="text-xs text-muted-foreground">
                          {formatDate(inquiry.createdAt || new Date().toISOString())}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">
                          {inquiry.message}
                        </p>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AdminDashboard;
